import React, { useState } from "react";
import {
  Bell, AlertTriangle, CheckCircle2, Info, Clock,
  Search, Filter, ChevronRight, ShieldAlert,
  Radio, Zap, Activity, Terminal, MessageSquare,
  ArrowUpRight, Settings2, Trash2
} from "lucide-react";
import InstrumentLoader from "@/components/InstrumentLoader";

// --- Sub-componente Stilate ---

const GlassCard = ({ children, className = "", title, icon: Icon, badge }: any) => (
  <div className={`bg-[#0F172A]/60 backdrop-blur-2xl border border-slate-800/50 rounded-3xl p-6 shadow-2xl flex flex-col hover:border-blue-500/30 transition-all duration-500 ${className}`}>
    {title && (
      <div className="flex items-center justify-between mb-6 border-b border-slate-800/50 pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-500/10 rounded-lg">
            {Icon && <Icon size={18} className="text-blue-400" />}
          </div>
          <div>
            <h3 className="text-xs font-black uppercase tracking-[0.2em] text-white">{title}</h3>
            <p className="text-[9px] font-mono text-slate-500 uppercase">Subsystem // Active</p>
          </div>
        </div>
        {badge && (
          <span className="text-[10px] font-black px-3 py-1 rounded-full bg-blue-600/10 text-blue-400 border border-blue-500/20">
            {badge}
          </span>
        )}
      </div>
    )}
    <div className="flex-1">{children}</div>
  </div>
);

const StatusBadge = ({ type }: { type: string }) => {
  const styles: any = {
    critical: "bg-red-500/10 text-red-500 border-red-500/20",
    warning: "bg-orange-500/10 text-orange-500 border-orange-500/20",
    success: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
    info: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  };
  return (
    <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded border ${styles[type]}`}>
      {type}
    </span>
  );
};

const NotificationRow = ({ notification }: any) => {
  const Icon = {
    critical: ShieldAlert,
    warning: AlertTriangle,
    success: CheckCircle2,
    info: Info
  }[notification.type as "critical" | "warning" | "success" | "info"] || Info;

  return (
    <div className="group relative flex items-center gap-6 p-4 rounded-2xl border border-transparent hover:border-slate-800 hover:bg-slate-900/40 transition-all cursor-pointer">
      <div className="flex flex-col items-center gap-1 min-w-[60px]">
        <span className="text-[10px] font-mono text-slate-500">{notification.time}</span>
        <div className={`w-1 h-8 rounded-full ${notification.type === 'critical' ? 'bg-red-500' : 'bg-slate-800'}`} />
      </div>

      <div className={`p-3 rounded-xl bg-slate-900 border border-slate-800 group-hover:scale-110 transition-transform`}>
        <Icon size={20} className={
          notification.type === 'critical' ? 'text-red-500' :
          notification.type === 'warning' ? 'text-orange-500' : 'text-blue-400'
        } />
      </div>

      <div className="flex-1">
        <div className="flex items-center gap-3 mb-1">
          <h4 className="text-sm font-bold text-slate-100 uppercase tracking-tight">{notification.title}</h4>
          <StatusBadge type={notification.type} />
        </div>
        <p className="text-xs text-slate-400 max-w-2xl line-clamp-1">{notification.message}</p>
      </div>

      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button className="p-2 hover:bg-blue-500/10 rounded-lg text-blue-400 transition-colors">
          <ArrowUpRight size={16} />
        </button>
        <button className="p-2 hover:bg-red-500/10 rounded-lg text-slate-500 hover:text-red-500 transition-colors">
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

// --- Componenta Principală ---

const NotificationsPage = () => {
  const [loading] = useState(false);

  const notifications = [
    { id: 1, type: "critical", title: "Sistem: Alertă Mentenanță", message: "Aeronava YR-DAA a depășit intervalul de 100h pentru inspecția motorului. Zborul este restricționat.", time: "14:20" },
    { id: 2, type: "warning", title: "METAR Update: LRBS", message: "Condiții meteo nefavorabile raportate la Aerodrom Băneasa. Vânt de forfecare (Windshear) detectat.", time: "13:45" },
    { id: 3, type: "success", title: "Validare Documente", message: "Licența PPL(A) a fost verificată și aprobată în sistemul de zbor.", time: "11:10" },
    { id: 4, type: "info", title: "Mesaj Turn Control", message: "Planul de zbor pentru ruta București - Constanța a fost recepționat de ROMATSA.", time: "09:05" },
    { id: 5, type: "info", title: "Alimentare Finalizată", message: "S-a confirmat alimentarea cu 80L AVGAS 100LL pentru YR-PHP.", time: "08:30" },
  ];

  return (
    <div className="min-h-screen bg-[#020617] text-slate-300 font-sans selection:bg-blue-500/30">
      {/* Background Decorative Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-600/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-5%] left-[-5%] w-[400px] h-[400px] bg-indigo-600/10 blur-[100px] rounded-full" />
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.03]" />
      </div>

      <InstrumentLoader visible={loading} />

      <div className="container max-w-7xl py-12 px-6 relative z-10">

        {/* HEADER */}
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12 border-l-4 border-blue-600 pl-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                <p className="text-blue-400 font-mono text-[10px] tracking-[0.4em] uppercase">Communication & Logs</p>
            </div>
            <h1 className="text-5xl font-black text-white tracking-tighter uppercase italic">
              Terminal <span className="text-slate-600">Notificări</span>
            </h1>
          </div>

          <div className="flex items-center gap-3">
             <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
                <input
                    type="text"
                    placeholder="Search logs..."
                    className="bg-slate-900/80 border border-slate-800 rounded-xl py-2 pl-10 pr-4 text-xs focus:ring-1 focus:ring-blue-500 outline-none w-64 transition-all"
                />
             </div>
             <button className="p-2 bg-slate-900 border border-slate-800 rounded-xl text-slate-400 hover:text-white transition-colors">
                <Filter size={18} />
             </button>
             <button className="p-2 bg-slate-900 border border-slate-800 rounded-xl text-slate-400 hover:text-white transition-colors">
                <Settings2 size={18} />
             </button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

          {/* PANOU STÂNGA: LIVE FEED (9 COLS) */}
          <div className="lg:col-span-8 space-y-6">
            <GlassCard title="Centralized Notification Feed" icon={Radio} badge={`${notifications.length} Active Alerts`}>
              <div className="space-y-2">
                {notifications.map((notif) => (
                  <NotificationRow key={notif.id} notification={notif} />
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-slate-800/50 flex justify-center">
                <button className="group flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 hover:text-blue-400 transition-all">
                  Load Older Transmission Logs
                  <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </GlassCard>
          </div>

          {/* PANOU DREAPTA: STATS & QUICK ACTIONS (4 COLS) */}
          <div className="lg:col-span-4 space-y-8">

            {/* System Status HUD */}
            <GlassCard title="System Health" icon={Activity}>
              <div className="space-y-6">
                {[
                  { label: "Data Uplink", val: "Active", color: "text-emerald-500" },
                  { label: "Encryption", val: "AES-256", color: "text-blue-400" },
                  { label: "Storage", val: "84%", color: "text-orange-400" }
                ].map(stat => (
                  <div key={stat.label} className="flex justify-between items-end border-b border-slate-800/30 pb-2">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{stat.label}</span>
                    <span className={`text-xs font-mono font-bold ${stat.color}`}>{stat.val}</span>
                  </div>
                ))}

                <div className="pt-4 flex gap-4">
                    <div className="flex-1 bg-slate-900/50 p-4 rounded-2xl border border-slate-800 text-center">
                        <p className="text-[20px] font-black text-white leading-none">12</p>
                        <p className="text-[8px] text-slate-500 uppercase font-bold mt-1">Unread</p>
                    </div>
                    <div className="flex-1 bg-slate-900/50 p-4 rounded-2xl border border-slate-800 text-center">
                        <p className="text-[20px] font-black text-orange-500 leading-none">02</p>
                        <p className="text-[8px] text-slate-500 uppercase font-bold mt-1">Critical</p>
                    </div>
                </div>
              </div>
            </GlassCard>

            {/* Quick Actions Panel */}
            <GlassCard title="Operations Console" icon={Terminal}>
               <div className="grid grid-cols-1 gap-3">
                 <button className="flex items-center justify-between p-4 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl transition-all group">
                    <div className="flex items-center gap-3">
                        <Zap size={18} />
                        <span className="text-[10px] font-black uppercase tracking-widest">Mark All Read</span>
                    </div>
                    <ChevronRight size={14} className="opacity-50 group-hover:translate-x-1 transition-all" />
                 </button>

                 <button className="flex items-center justify-between p-4 bg-slate-900 border border-slate-800 hover:border-slate-600 text-slate-300 rounded-2xl transition-all group">
                    <div className="flex items-center gap-3">
                        <MessageSquare size={18} />
                        <span className="text-[10px] font-black uppercase tracking-widest">Chat Support</span>
                    </div>
                    <span className="w-2 h-2 bg-emerald-500 rounded-full shadow-[0_0_10px_rgba(16,185,129,0.5)]" />
                 </button>
               </div>
            </GlassCard>

          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;